import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { Button } from 'antd';
import { getAuth } from 'firebase/auth';
import { useEffect } from 'react';
import { z } from 'zod';

import { getGetAccountQueryKey } from '../../api';
import { ResetPassword } from '../../modules/auth/components/reset-password';
import { VerifyEmail } from '../../modules/auth/components/verify-email';
import { CenteredLayoutContent } from '../../ui/components/layout';

const AuthActionRouteSearchSchema = z.object({
  mode: z
    .enum(['resetPassword', 'verifyEmail', 'changeEmail', 'verifyAndChangeEmail', 'signIn'])
    .optional(),
  oobCode: z.coerce.string().optional(),
});

const AuthActionRoute = () => {
  const { mode, oobCode } = useSearch({ from: '/auth/action' });
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();

  const isChangeEmailMode = mode === 'changeEmail' || mode === 'verifyAndChangeEmail';

  useEffect(() => {
    if (mode === 'signIn') {
      const url = new URL(window.location.href);
      url.pathname = '/auth/signin';
      window.location.replace(url.toString());
    }
  }, [mode]);

  if (mode === 'signIn') {
    return null;
  }

  if (mode === 'resetPassword') {
    return <ResetPassword oobCode={oobCode ?? null} />;
  }

  if (mode === 'verifyEmail' || isChangeEmailMode) {
    if (isChangeEmailMode && !oobCode) {
      return (
        <CenteredLayoutContent
          title="Invalid action"
          subtitle="The email change link is invalid. Please request a new one from Security settings."
        >
          <Button
            type="primary"
            size="large"
            block
            onClick={() => {
              navigate({ to: '/settings/security', replace: true });
            }}
          >
            Back to Security
          </Button>
        </CenteredLayoutContent>
      );
    }

    return (
      <VerifyEmail
        oobCode={oobCode ?? null}
        onSignedOut={() => {
          navigate({ to: '/auth/signin', replace: true });
        }}
        onActionApplied={async () => {
          if (!isChangeEmailMode) {
            return;
          }

          await getAuth().currentUser?.getIdToken(true);
          queryClient.resetQueries({ queryKey: getGetAccountQueryKey() });
          router.invalidate();
        }}
        onVerified={() => {
          navigate({ to: isChangeEmailMode ? '/settings/security' : '/', replace: true });
        }}
      />
    );
  }

  return (
    <CenteredLayoutContent
      title="Invalid action"
      subtitle="The link you followed is invalid. Please request a new action link."
    >
      <Button
        type="primary"
        size="large"
        block
        onClick={() => {
          navigate({ to: '/auth/forgot-password' });
        }}
      >
        Go to Forgot Password
      </Button>
    </CenteredLayoutContent>
  );
};

export const Route = createFileRoute('/auth/action')({
  validateSearch: (params) => AuthActionRouteSearchSchema.parse(params),
  component: AuthActionRoute,
});
